import { ExternalLink, Phone, MapPin, Users, DollarSign, HeartHandshake, ShieldCheck, Building2, Mail } from 'lucide-react';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { useState, useEffect } from 'react';
import { ListOrganizationModal } from './ListOrganizationModal';

// ─── Resource type definitions ────────────────────────────────────────────────

export type ResourceCategory = 'peer' | 'financial' | 'program' | 'advocacy';

export interface CareResource {
  name: string;
  org?: string;
  description: string;
  url: string;
  phone?: string;
  category: ResourceCategory;
  /** If set, only surfaces when userState is in this list */
  states?: string[];
  /** Tags that trigger inclusion of this resource */
  tags: string[];
  /** Show in prominent featured section above kanban */
  featured?: boolean;
  /** Specific ways this org helps (e.g., ["Medications", "Case Management"]) */
  helpTypes?: string[];
  /** Short mission statement */
  mission?: string;
}

export const CATEGORY_META: Record<ResourceCategory, { label: string; color: string; Icon: React.ElementType }> = {
  peer:      { label: 'Peer Support & Community',    color: '#7B5EA7', Icon: Users },
  financial: { label: 'Financial Assistance',         color: 'var(--civic-money)', Icon: DollarSign },
  program:   { label: 'Programs & Services',          color: 'var(--civic-navy)', Icon: ShieldCheck },
  advocacy:  { label: 'Patient Advocacy & Rights',   color: 'var(--civic-red)', Icon: HeartHandshake },
};

export const CATEGORY_ORDER: ResourceCategory[] = ['financial', 'program', 'peer', 'advocacy'];

// ─── Resource database ────────────────────────────────────────────────────────
// Tags match scenario tags from the database. Each resource appears if
// the scenario shares ANY of its tags. 'general' surfaces on every scenario.

export const RESOURCES: CareResource[] = [

  // ── PREGNANCY / MATERNAL ──────────────────────────────────────────────────
  {
    name: 'Medicaid & CHIP for Pregnant Women',
    org: 'CMS / State Medicaid',
    description: 'Most states offer full Medicaid coverage for pregnancy regardless of income. Covers prenatal visits, delivery, and 60 days postpartum. Apply immediately — coverage can be retroactive.',
    url: 'https://www.medicaid.gov/medicaid/eligibility/index.html',
    category: 'financial',
    tags: ['pregnancy', 'maternal', 'obstetrics', 'prenatal', 'postpartum', 'childbirth'],
  },
  {
    name: 'WIC (Women, Infants & Children)',
    org: 'USDA Food & Nutrition Service',
    description: 'Free nutritious foods, breastfeeding support, and referrals for pregnant women, new mothers, and children up to age 5.',
    url: 'https://www.fns.usda.gov/wic',
    phone: '1-800-942-3678',
    category: 'program',
    tags: ['pregnancy', 'maternal', 'prenatal', 'postpartum', 'childbirth'],
  },
  {
    name: 'March of Dimes',
    description: 'Peer support, education, and advocacy for pregnancy health. Includes community forums and resources for high-risk pregnancies and NICU families.',
    url: 'https://www.marchofdimes.org',
    phone: '1-888-663-4637',
    category: 'peer',
    tags: ['pregnancy', 'maternal', 'prenatal', 'postpartum', 'childbirth', 'nicu'],
    featured: true,
    mission: 'Fighting for the health of all moms and babies through research, education, and advocacy.',
    helpTypes: ['Peer Support', 'Education', 'NICU Families', 'Advocacy'],
  },
  {
    name: 'Postpartum Support International',
    description: 'Helpline, online support groups, and provider directory for postpartum depression, anxiety, and perinatal mood disorders.',
    url: 'https://www.postpartum.net',
    phone: '1-800-944-4773',
    category: 'peer',
    tags: ['pregnancy', 'maternal', 'postpartum', 'childbirth'],
  },
  {
    name: 'Title X Family Planning Clinics',
    org: 'HHS Office of Population Affairs',
    description: 'Federally funded clinics offering free or low-cost prenatal care, family planning, and STI services based on income.',
    url: 'https://opa.hhs.gov/title-x-family-planning',
    category: 'program',
    tags: ['pregnancy', 'maternal', 'prenatal', 'childbirth'],
  },

  // ── HIV / AIDS ────────────────────────────────────────────────────────────
  {
    name: 'Ryan White HIV/AIDS Program',
    org: 'HRSA / Federal',
    description: 'Federal program covering medical care, medications, and support services for low-income people living with HIV. Contact your local Ryan White recipient for services.',
    url: 'https://ryanwhite.hrsa.gov',
    phone: '1-800-448-0440',
    category: 'financial',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
    featured: true,
    mission: 'Providing a comprehensive system of care for people living with HIV who are uninsured or underinsured.',
    helpTypes: ['Medications', 'Medical Care', 'Support Services', 'Case Management'],
  },
  {
    name: 'AIDS Drug Assistance Program (ADAP)',
    org: 'State Programs',
    description: 'State-run programs that pay for HIV medications when insurance doesn\'t cover them. Each state has its own income limits and formulary.',
    url: 'https://nastad.org/adap',
    category: 'financial',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
  },
  {
    name: 'The Well Project',
    description: 'Peer support and education specifically for women living with HIV. Includes a global community of women, forums, and a comprehensive resource library.',
    url: 'https://www.thewellproject.org',
    category: 'peer',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
  },
  {
    name: 'POZ Community Forums',
    description: 'Long-running peer community for people living with HIV/AIDS. Discussion boards covering treatment, mental health, relationships, and daily life.',
    url: 'https://forums.poz.com',
    category: 'peer',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
  },
  {
    name: 'AIDS United',
    description: 'Emergency financial assistance, advocacy, and connection to local AIDS service organizations nationwide.',
    url: 'https://www.aidsunited.org',
    phone: '1-202-408-4848',
    category: 'advocacy',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
  },
  {
    name: 'Texas HIV Medication Program',
    org: 'Texas DSHS',
    description: 'Free antiretroviral medications for Texas residents living with HIV who are uninsured or underinsured.',
    url: 'https://www.dshs.texas.gov/hivstd/meds',
    category: 'financial',
    tags: ['hiv', 'aids', 'antiretroviral', 'hiv-aids'],
    states: ['TX'],
  },

  // ── CANCER ───────────────────────────────────────────────────────────────
  {
    name: 'American Cancer Society',
    description: 'Peer support groups (in-person and online), rides to treatment, lodging, and financial assistance. 24/7 helpline staffed by oncology social workers.',
    url: 'https://www.cancer.org',
    phone: '1-800-227-2345',
    category: 'peer',
    tags: ['cancer', 'oncology', 'chemotherapy', 'radiation', 'tumor', 'lymphoma', 'leukemia', 'breast-cancer', 'lung-cancer', 'colon-cancer'],
    featured: true,
    mission: 'Improving the lives of people with cancer and their families through advocacy, research, and patient support.',
    helpTypes: ['Support Groups', 'Rides to Treatment', 'Lodging', 'Financial Assistance'],
  },
  {
    name: 'Cancer Support Community',
    description: 'Free counseling, support groups, educational workshops, and wellness programs for cancer patients and caregivers. 175+ locations nationwide.',
    url: 'https://www.cancersupportcommunity.org',
    phone: '1-888-793-9355',
    category: 'peer',
    tags: ['cancer', 'oncology', 'chemotherapy', 'radiation', 'tumor', 'lymphoma', 'leukemia', 'breast-cancer'],
  },
  {
    name: 'CancerCare',
    description: 'Free financial assistance (up to $500), counseling, and case management. Also connects patients to transportation assistance and other grants.',
    url: 'https://www.cancercare.org',
    phone: '1-800-813-4673',
    category: 'financial',
    tags: ['cancer', 'oncology', 'chemotherapy', 'radiation', 'tumor', 'lymphoma', 'leukemia', 'breast-cancer'],
  },
  {
    name: 'Patient Advocate Foundation Co-Pay Relief',
    description: 'Direct financial assistance for co-pays, premiums, and treatment costs for specific cancer diagnoses. Multiple disease funds available.',
    url: 'https://www.copays.org',
    phone: '1-866-512-3861',
    category: 'financial',
    tags: ['cancer', 'oncology', 'chemotherapy', 'tumor', 'lymphoma', 'leukemia', 'breast-cancer'],
  },

  // ── DIABETES ─────────────────────────────────────────────────────────────
  {
    name: 'American Diabetes Association',
    description: 'Education resources, peer support programs, advocacy, and the Diabetes Stops Here community. Includes local chapter events and online forums.',
    url: 'https://www.diabetes.org',
    phone: '1-800-342-2383',
    category: 'peer',
    tags: ['diabetes', 'type-1-diabetes', 'type-2-diabetes', 'insulin', 'a1c', 'metabolic'],
    featured: true,
    mission: 'Preventing and curing diabetes and improving the lives of all people affected by diabetes.',
    helpTypes: ['Education', 'Peer Support', 'Advocacy', 'Community Events'],
  },
  {
    name: 'Beyond Type 1 / Beyond Type 2',
    description: 'Active online peer community for people with Type 1 and Type 2 diabetes. App-based forums, mentorship matching, and real-time support.',
    url: 'https://beyondtype1.org',
    category: 'peer',
    tags: ['diabetes', 'type-1-diabetes', 'type-2-diabetes', 'insulin'],
  },
  {
    name: 'National Diabetes Prevention Program',
    org: 'CDC',
    description: 'Evidence-based lifestyle change program proven to prevent or delay Type 2 diabetes. Free or low-cost classes available in-person and online.',
    url: 'https://www.cdc.gov/diabetes/prevention',
    category: 'program',
    tags: ['diabetes', 'type-2-diabetes', 'prediabetes', 'metabolic'],
  },
  {
    name: 'Insulin Help',
    description: 'Connects patients with manufacturer patient assistance programs and state programs for free or discounted insulin.',
    url: 'https://www.insulinhelp.org',
    category: 'financial',
    tags: ['diabetes', 'type-1-diabetes', 'type-2-diabetes', 'insulin'],
  },

  // ── CARDIAC / HEART ───────────────────────────────────────────────────────
  {
    name: 'Mended Hearts',
    description: 'Peer visitation program and support groups for heart disease patients and their families. Hospital-based and virtual chapters.',
    url: 'https://mendedhearts.org',
    phone: '1-888-432-7899',
    category: 'peer',
    tags: ['cardiac', 'heart', 'cardiovascular', 'heart-failure', 'bypass', 'stent', 'valve'],
    featured: true,
    mission: 'Inspiring hope and improving the quality of life for heart patients and their families.',
    helpTypes: ['Peer Visitation', 'Support Groups', 'Cardiac Rehab', 'Caregiver Support'],
  },
  {
    name: 'American Heart Association Support',
    description: 'Online peer community, cardiac rehab resources, and financial assistance programs for heart patients.',
    url: 'https://www.heart.org',
    phone: '1-800-242-8721',
    category: 'peer',
    tags: ['cardiac', 'heart', 'cardiovascular', 'heart-failure', 'bypass', 'stent', 'valve', 'stroke'],
  },
  {
    name: 'HeartLine (AHA App)',
    description: 'FDA-authorized app connecting heart patients and caregivers to real-time monitoring, health journaling, and AHA clinical trials.',
    url: 'https://heartline.com',
    category: 'program',
    tags: ['cardiac', 'heart', 'cardiovascular', 'atrial-fibrillation'],
  },

  // ── MENTAL HEALTH ─────────────────────────────────────────────────────────
  {
    name: 'NAMI (National Alliance on Mental Illness)',
    description: 'Free peer support groups (NAMI Connection), family support (NAMI Family Support Group), education programs, and a helpline staffed by trained volunteers.',
    url: 'https://www.nami.org',
    phone: '1-800-950-6264',
    category: 'peer',
    tags: ['mental-health', 'depression', 'anxiety', 'bipolar', 'schizophrenia', 'psychiatric', 'behavioral'],
    featured: true,
    mission: 'Building better lives for the millions of Americans affected by mental illness.',
    helpTypes: ['Peer Support Groups', 'Family Support', 'Education', 'Helpline'],
  },
  {
    name: 'SAMHSA National Helpline',
    description: 'Free, confidential 24/7 treatment referral and information service for mental health and substance use disorders.',
    url: 'https://www.samhsa.gov/find-help/national-helpline',
    phone: '1-800-662-4357',
    category: 'program',
    tags: ['mental-health', 'depression', 'anxiety', 'bipolar', 'psychiatric', 'substance-use', 'addiction', 'behavioral'],
  },
  {
    name: 'Depression and Bipolar Support Alliance',
    description: 'Online and in-person peer support groups for depression and bipolar disorder. Wellness tools and personal stories.',
    url: 'https://www.dbsalliance.org',
    phone: '1-800-826-3632',
    category: 'peer',
    tags: ['mental-health', 'depression', 'bipolar', 'psychiatric'],
  },

  // ── SUBSTANCE USE / ADDICTION ─────────────────────────────────────────────
  {
    name: 'SMART Recovery',
    description: 'Science-based addiction recovery peer support groups (alternative to 12-step). Free in-person and online meetings worldwide.',
    url: 'https://www.smartrecovery.org',
    category: 'peer',
    tags: ['substance-use', 'addiction', 'alcohol', 'opioid', 'recovery'],
    featured: true,
    mission: 'Helping people recover from addictive behaviors using science-based tools and peer support.',
    helpTypes: ['Peer Support', 'Online Meetings', 'Self-Help Tools', 'Family Support'],
  },
  {
    name: 'SAMHSA Treatment Locator',
    description: 'Find nearby addiction treatment facilities, including those accepting Medicaid, sliding-scale fees, or offering free services.',
    url: 'https://findtreatment.gov',
    phone: '1-800-662-4357',
    category: 'program',
    tags: ['substance-use', 'addiction', 'alcohol', 'opioid', 'recovery'],
  },

  // ── KIDNEY / RENAL ────────────────────────────────────────────────────────
  {
    name: 'American Kidney Fund',
    description: 'Direct financial assistance for dialysis patients, kidney transplant recipients, and those with chronic kidney disease. Also covers health insurance premiums.',
    url: 'https://www.kidneyfund.org',
    phone: '1-800-638-8299',
    category: 'financial',
    tags: ['kidney', 'renal', 'dialysis', 'transplant', 'ckd'],
    featured: true,
    mission: 'Helping people fight kidney disease and live healthier lives through direct financial support.',
    helpTypes: ['Financial Assistance', 'Insurance Premiums', 'Dialysis Support', 'Transplant Aid'],
  },
  {
    name: 'National Kidney Foundation',
    description: 'Peer support, disease management resources, clinical trial matching, and advocacy. Includes the Peers program connecting patients with trained peer mentors.',
    url: 'https://www.kidney.org',
    phone: '1-800-622-9010',
    category: 'peer',
    tags: ['kidney', 'renal', 'dialysis', 'transplant', 'ckd'],
  },

  // ── ORTHOPEDIC / JOINT / SPINE ────────────────────────────────────────────
  {
    name: 'Arthritis Foundation',
    description: 'Peer support groups, physical activity programs, advocacy, and drug assistance information for arthritis and joint replacement patients.',
    url: 'https://www.arthritis.org',
    phone: '1-800-283-7800',
    category: 'peer',
    tags: ['arthritis', 'joint-replacement', 'knee', 'hip', 'orthopedic', 'rheumatoid'],
    featured: true,
    mission: 'Conquering arthritis for people who live with it every day through advocacy and support.',
    helpTypes: ['Support Groups', 'Exercise Programs', 'Drug Assistance', 'Advocacy'],
  },
  {
    name: 'American Chronic Pain Association',
    description: 'Peer support groups and self-management tools for people living with chronic pain from spine or joint conditions.',
    url: 'https://theacpa.org',
    phone: '1-800-533-3231',
    category: 'peer',
    tags: ['chronic-pain', 'spine', 'back', 'orthopedic', 'joint-replacement'],
  },

  // ── RESPIRATORY / PULMONARY ───────────────────────────────────────────────
  {
    name: 'American Lung Association',
    description: 'Lung disease support groups, Better Breathers Club (COPD), and lung cancer support. Helpline with trained lung health specialists.',
    url: 'https://www.lung.org',
    phone: '1-800-586-4872',
    category: 'peer',
    tags: ['respiratory', 'pulmonary', 'copd', 'asthma', 'lung-cancer', 'pneumonia'],
    featured: true,
    mission: 'Saving lives by improving lung health and preventing lung disease.',
    helpTypes: ['Support Groups', 'Better Breathers Club', 'Lung Cancer Support', 'Helpline'],
  },
  {
    name: 'COPD Foundation',
    description: 'COPD360social peer network, education, and advocacy. Includes a nurse-staffed helpline and clinical trial matching.',
    url: 'https://www.copdfoundation.org',
    phone: '1-866-316-2673',
    category: 'peer',
    tags: ['copd', 'respiratory', 'pulmonary'],
  },

  // ── ORGAN TRANSPLANT ─────────────────────────────────────────────────────
  {
    name: 'National Transplant Assistance Fund',
    description: 'Fundraising and financial assistance for transplant patients and living donors to cover non-medical expenses.',
    url: 'https://www.transplantfund.org',
    phone: '1-800-642-8399',
    category: 'financial',
    tags: ['transplant', 'kidney-transplant', 'liver-transplant', 'heart-transplant'],
  },
  {
    name: 'Transplant Living (UNOS)',
    description: 'Peer forums, patient stories, and resource guides for every stage of the transplant journey from UNOS.',
    url: 'https://transplantliving.org',
    category: 'peer',
    tags: ['transplant', 'kidney-transplant', 'liver-transplant', 'heart-transplant'],
  },

  // ── RARE DISEASE ─────────────────────────────────────────────────────────
  {
    name: 'NORD (National Organization for Rare Disorders)',
    description: 'Disease-specific support groups, patient registry, financial assistance, and advocacy for rare disease patients.',
    url: 'https://rarediseases.org',
    phone: '1-203-744-0100',
    category: 'advocacy',
    tags: ['rare-disease', 'genetic', 'rare'],
    featured: true,
    mission: 'Providing support for individuals with rare diseases through advocacy, education, and research.',
    helpTypes: ['Support Groups', 'Patient Registry', 'Financial Assistance', 'Advocacy'],
  },

  // ── GENERAL (shown on all scenarios) ─────────────────────────────────────
  {
    name: 'NeedyMeds',
    description: 'Database of 22,000+ prescription assistance programs, disease-based assistance, free clinics, and sliding-scale facilities near you.',
    url: 'https://www.needymeds.org',
    phone: '1-800-503-6897',
    category: 'financial',
    tags: ['general'],
  },
  {
    name: 'Patient Advocate Foundation',
    description: 'Free case management services helping patients resolve insurance denials, access treatments, and navigate financial hardship.',
    url: 'https://www.patientadvocate.org',
    phone: '1-800-532-5274',
    category: 'advocacy',
    tags: ['general'],
  },
  {
    name: 'Hill-Burton Free & Reduced-Cost Care',
    org: 'HRSA',
    description: 'Hospitals that received federal construction funding are legally required to provide free or reduced-cost care. Search for facilities near you.',
    url: 'https://www.hrsa.gov/get-health-care/affordable/hill-burton',
    phone: '1-800-638-0742',
    category: 'financial',
    tags: ['general'],
  },
  {
    name: 'Health Resources & Services Administration (HRSA)',
    description: 'Find federally funded health centers offering care on a sliding-scale based on your income — near every zip code in the US.',
    url: 'https://findahealthcenter.hrsa.gov',
    phone: '1-888-275-4772',
    category: 'program',
    tags: ['general'],
  },
  {
    name: 'State Medicaid / CHIP Program Finder',
    org: 'CMS',
    description: 'Check your eligibility for Medicaid and CHIP. Many adults with low income qualify — especially if pregnant, have a disability, or care for a child.',
    url: 'https://www.healthcare.gov/medicaid-chip',
    category: 'financial',
    tags: ['general'],
  },
  {
    name: 'HealthWell Foundation',
    description: 'Disease fund grants covering premiums, co-pays, and deductibles for patients with specific conditions. Applies to many chronic and serious illnesses.',
    url: 'https://www.healthwellfoundation.org',
    phone: '1-800-675-8416',
    category: 'financial',
    tags: ['general'],
  },
  {
    name: 'RxAssist — Patient Assistance Programs',
    description: 'Search engine for 3,000+ manufacturer patient assistance programs offering free or discounted medications.',
    url: 'https://www.rxassist.org',
    category: 'financial',
    tags: ['general'],
  },
  {
    name: 'Benefits.gov',
    org: 'Federal Government',
    description: 'Official US benefits finder — search all federal benefit programs you may qualify for based on your situation.',
    url: 'https://www.benefits.gov',
    category: 'program',
    tags: ['general'],
  },
];

// ─── Tag matching ─────────────────────────────────────────────────────────────

export function matchResources(scenarioTags: string[], scenarioName: string, userState?: string): CareResource[] {
  const tagSet = new Set(scenarioTags.map(t => t.toLowerCase()));

  // Also extract keywords from scenario name for fallback matching
  const nameLower = scenarioName.toLowerCase();

  const matched = RESOURCES.filter(r => {
    // Always include general resources
    if (r.tags.includes('general')) return true;

    // State filter: if resource is state-specific, only include if state matches
    if (r.states && r.states.length > 0) {
      if (!userState || !r.states.includes(userState)) return false;
    }

    // Tag overlap
    if (r.tags.some(t => tagSet.has(t))) return true;

    // Fallback: keyword in scenario name
    return r.tags.some(t => nameLower.includes(t.replace(/-/g, ' ')));
  });

  return matched;
}

// ─── Kanban colors (dark-mode friendly, matches Follow page) ─────────────────

const KANBAN_COLORS: Record<ResourceCategory, { light: string; dark: string }> = {
  financial: { light: '#059669', dark: '#34d399' },
  program:   { light: '#2563eb', dark: '#60a5fa' },
  peer:      { light: '#7c3aed', dark: '#a78bfa' },
  advocacy:  { light: '#dc2626', dark: '#f87171' },
};

const KANBAN_ICONS: Record<ResourceCategory, React.ElementType> = {
  financial: DollarSign,
  program: ShieldCheck,
  peer: Users,
  advocacy: HeartHandshake,
};

const KANBAN_LABELS: Record<ResourceCategory, string> = {
  financial: 'Financial Assistance',
  program: 'Programs & Services',
  peer: 'Peer Support',
  advocacy: 'Advocacy & Rights',
};

// ─── Resource card (animated, matches Follow page) ───────────────────────────

function ResourceCard({
  resource,
  color,
  isDark,
  index,
}: {
  resource: CareResource;
  color: string;
  isDark: boolean;
  index: number;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 60 + index * 80);
    return () => clearTimeout(t);
  }, [index]);

  return (
    <div
      className="rounded-lg p-3 transition-all duration-300"
      style={{
        backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}`,
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(12px)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1.5">
        <span
          className="text-sm font-semibold leading-tight"
          style={{ color: isDark ? '#f0f0f0' : '#1a1a1a' }}
        >
          {resource.name}
        </span>
        {resource.states && resource.states.length > 0 && (
          <span
            className="flex-shrink-0 inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: `${color}20`, color }}
          >
            <MapPin className="w-2 h-2" />
            {resource.states.join(', ')}
          </span>
        )}
      </div>
      {resource.org && (
        <div className="text-[11px] mb-1" style={{ color: isDark ? '#9ca3af' : '#6b7280' }}>
          {resource.org}
        </div>
      )}
      <p
        className="text-xs leading-relaxed mb-2"
        style={{ color: isDark ? '#b0b0b0' : '#555' }}
      >
        {resource.description}
      </p>
      <div className="flex items-center gap-3 flex-wrap">
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-medium flex items-center gap-1 hover:underline"
          style={{ color }}
        >
          <ExternalLink className="w-3 h-3" />
          {(() => { try { return new URL(resource.url).hostname.replace('www.', ''); } catch { return 'Visit'; } })()}
        </a>
        {resource.phone && (
          <a
            href={`tel:${resource.phone.replace(/\D/g, '')}`}
            className="text-xs font-medium flex items-center gap-1 hover:underline"
            style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
          >
            <Phone className="w-3 h-3" />
            {resource.phone}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Featured Organization Card ──────────────────────────────────────────────

function FeaturedOrgCard({
  resource,
  isDark,
  isAhp,
  index,
}: {
  resource: CareResource;
  isDark: boolean;
  isAhp: boolean;
  index: number;
}) {
  const [visible, setVisible] = useState(false);
  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 80 + index * 100);
    return () => clearTimeout(t);
  }, [index]);

  const categoryColor = isDark ? KANBAN_COLORS[resource.category].dark : KANBAN_COLORS[resource.category].light;

  return (
    <div
      className="rounded-xl p-5 transition-all duration-300 flex flex-col"
      style={{
        backgroundColor: isDark ? 'var(--civic-card-dark)' : '#fff',
        border: `1px solid ${isDark ? 'rgba(var(--civic-gold-rgb),0.2)' : 'rgba(var(--civic-ink-rgb),0.12)'}`,
        boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.3)' : '0 2px 12px rgba(0,0,0,0.08)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <h3
          className="text-base font-semibold leading-snug"
          style={{ color: isDark ? 'var(--civic-warm)' : 'var(--civic-ink)', fontFamily: isAhp ? serif : undefined }}
        >
          <Building2 className="w-4 h-4 inline-block mr-1.5 -mt-0.5" style={{ color: categoryColor }} />
          {resource.name}
        </h3>
      </div>
      {resource.org && (
        <div
          className="text-xs mb-2 font-medium"
          style={{ color: isDark ? '#9ca3af' : '#6b7280', fontFamily: isAhp ? sans : undefined }}
        >
          {resource.org}
        </div>
      )}

      {resource.mission && (
        <p
          className="text-sm leading-relaxed mb-3 italic"
          style={{ color: isDark ? '#b0b8c4' : '#444', fontFamily: isAhp ? sans : undefined }}
        >
          {resource.mission}
        </p>
      )}

      <p
        className="text-xs leading-relaxed mb-3"
        style={{ color: isDark ? '#9ca3af' : '#555', fontFamily: isAhp ? sans : undefined }}
      >
        {resource.description}
      </p>

      {resource.helpTypes && resource.helpTypes.length > 0 && (
        <div className="mb-3">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5" style={{ color: isDark ? '#9ca3af' : '#6b7280', fontFamily: isAhp ? sans : undefined }}>
            How they help
          </div>
          <div className="flex flex-wrap gap-1.5">
            {resource.helpTypes.map(ht => (
              <span
                key={ht}
                className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                style={{
                  backgroundColor: isDark ? 'rgba(var(--civic-gold-rgb),0.15)' : 'rgba(var(--civic-navy-rgb),0.08)',
                  color: isDark ? 'var(--civic-gold)' : 'var(--civic-navy)',
                  fontFamily: isAhp ? sans : undefined,
                }}
              >
                {ht}
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto flex items-center gap-4 pt-2" style={{ borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}` }}>
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs font-semibold flex items-center gap-1 hover:underline"
          style={{ color: categoryColor }}
        >
          <ExternalLink className="w-3 h-3" />
          {(() => { try { return new URL(resource.url).hostname.replace('www.', ''); } catch { return 'Visit'; } })()}
        </a>
        {resource.phone && (
          <a
            href={`tel:${resource.phone.replace(/\D/g, '')}`}
            className="text-xs font-medium flex items-center gap-1 hover:underline"
            style={{ color: isDark ? '#9ca3af' : '#6b7280' }}
          >
            <Phone className="w-3 h-3" />
            {resource.phone}
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

interface ScenarioCareResourcesProps {
  scenarioTags: string[];
  scenarioName: string;
  userState?: string;
}

export function ScenarioCareResources({ scenarioTags, scenarioName, userState }: ScenarioCareResourcesProps) {
  const brand = useBrand();
  const isAhp = brand.design === 'civic';
  const { isDark } = useTheme();
  const [listOrgOpen, setListOrgOpen] = useState(false);

  const resources = matchResources(scenarioTags, scenarioName, userState);

  // Group by category, in order
  const grouped = CATEGORY_ORDER.reduce<Record<ResourceCategory, CareResource[]>>(
    (acc, cat) => {
      acc[cat] = resources.filter(r => r.category === cat);
      return acc;
    },
    { peer: [], financial: [], program: [], advocacy: [] }
  );

  const featuredResources = resources.filter(r => r.featured);
  const totalCount = resources.length;
  const stateSpecificCount = resources.filter(r => r.states && r.states.length > 0).length;

  if (totalCount === 0) return null;

  const serif = "'Instrument Serif', Georgia, serif";
  const sans = "'Instrument Sans', system-ui, sans-serif";
  const ink = isDark ? 'var(--civic-warm)' : 'var(--civic-ink)';
  const muted = isDark ? '#9ca3af' : '#6b7280';

  return (
    <section
      id="connect-to-care"
      className="w-full mt-8 pb-4 rounded-xl"
      style={{
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.03)',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        padding: '24px 16px 16px',
      }}
    >
      {/* Section header */}
      <div className="text-center mb-6">
        <h2
          className={isAhp ? 'text-xl md:text-2xl mb-1' : 'text-xl md:text-2xl font-mono text-matrix-green mb-1'}
          style={isAhp ? { fontFamily: serif, color: ink, fontWeight: 400 } : undefined}
        >
          <HeartHandshake className="w-5 h-5 inline-block mr-2 -mt-0.5" style={isAhp ? { color: 'var(--civic-red)' } : undefined} />
          Connect to Care
        </h2>
        <p className="text-sm mb-1" style={isAhp ? { color: muted, fontFamily: sans } : { color: muted }}>
          Non-profit organizations, financial assistance, and support communities for your care journey
        </p>
        <p className="text-xs flex items-center justify-center gap-2 flex-wrap" style={isAhp ? { color: muted, fontFamily: sans } : undefined}>
          <span>
            {totalCount} resources matched to this scenario
            {userState ? ` · ${userState}` : ''}
          </span>
          {stateSpecificCount > 0 && userState && (
            <span
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
              style={{ backgroundColor: isDark ? 'rgba(var(--civic-money-soft-rgb),0.15)' : 'rgba(var(--civic-money-rgb),0.1)', color: isDark ? 'var(--civic-money-soft)' : 'var(--civic-money)' }}
            >
              <MapPin className="w-2.5 h-2.5" />
              {stateSpecificCount} state-specific
            </span>
          )}
          {!userState && (
            <span className="text-muted-foreground italic">· set your location to unlock state programs</span>
          )}
        </p>
      </div>

      {/* Featured Organizations */}
      {featuredResources.length > 0 && (
        <div className="mb-6">
          <h3
            className="text-sm font-bold uppercase tracking-wider mb-3 flex items-center gap-2"
            style={{ color: isDark ? 'var(--civic-gold)' : 'var(--civic-navy)', fontFamily: isAhp ? sans : undefined }}
          >
            <Building2 className="w-4 h-4" />
            Featured Organizations
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {featuredResources.map((r, i) => (
              <FeaturedOrgCard
                key={r.name}
                resource={r}
                isDark={isDark}
                isAhp={isAhp}
                index={i}
              />
            ))}
          </div>
        </div>
      )}

      {/* Kanban board — 4 columns on xl, 2 on md, 1 on mobile */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {CATEGORY_ORDER.map(cat => {
          const items = grouped[cat];
          const colors = KANBAN_COLORS[cat];
          const color = isDark ? colors.dark : colors.light;
          const Icon = KANBAN_ICONS[cat];

          return (
            <div
              key={cat}
              className="rounded-xl overflow-hidden flex flex-col"
              style={{
                backgroundColor: isDark ? '#1e293b' : '#fff',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
                boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 2px 12px rgba(0,0,0,0.1)',
                minHeight: 200,
              }}
            >
              {/* Column header */}
              <div
                className="px-4 py-3 flex items-center justify-between flex-shrink-0"
                style={{
                  borderBottom: `2px solid ${color}`,
                  backgroundColor: isDark ? `${color}15` : `${color}0a`,
                }}
              >
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color }} />
                  <span className="text-xs font-bold uppercase tracking-wider" style={{ color, fontFamily: isAhp ? sans : undefined }}>
                    {KANBAN_LABELS[cat]}
                  </span>
                </div>
                <span
                  className="text-[11px] font-bold px-2 py-0.5 rounded-full"
                  style={{ backgroundColor: `${color}15`, color }}
                >
                  {items.length}
                </span>
              </div>

              {/* Column body */}
              <div className="flex-1 p-3 space-y-2 overflow-y-auto kanban-scroll" style={{ maxHeight: 480 }}>
                {items.length === 0 ? (
                  <div className="flex items-center justify-center h-24 text-xs" style={{ color: muted }}>
                    No matches
                  </div>
                ) : (
                  items.map((r, i) => (
                    <ResourceCard
                      key={`${r.name}-${i}`}
                      resource={r}
                      color={color}
                      isDark={isDark}
                      index={i}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer note */}
      <p className="text-center text-[11px] mt-4 italic" style={{ color: muted, fontFamily: isAhp ? sans : undefined }}>
        Resources are matched based on your scenario. Always verify eligibility directly with each organization.
        {!userState && ' Set your location above for state-specific programs like ADAP, Medicaid, and local support groups.'}
      </p>

      {/* List Your Organization CTA */}
      <div
        className="mt-6 rounded-lg p-4 text-center"
        style={{
          backgroundColor: isDark ? 'rgba(var(--civic-gold-rgb),0.08)' : 'rgba(var(--civic-navy-rgb),0.04)',
          border: `1px dashed ${isDark ? 'rgba(var(--civic-gold-rgb),0.25)' : 'rgba(var(--civic-navy-rgb),0.2)'}`,
        }}
      >
        <p className="text-sm mb-2" style={{ color: isDark ? '#e5e7eb' : 'var(--civic-ink)', fontFamily: isAhp ? sans : undefined }}>
          <Building2 className="w-4 h-4 inline-block mr-1.5 -mt-0.5" style={{ color: isDark ? 'var(--civic-gold)' : 'var(--civic-navy)' }} />
          Do you represent a non-profit or organization that helps patients?
        </p>
        {/* Was a mailto: until 2026-08-02. A mailto is a dead end for most visitors —
            no configured mail client, or a registered webmail protocol handler that
            hijacks it onto an unrelated domain — which is a poor way to treat the one
            CTA aimed at people volunteering to help patients. Now a real form
            (ListOrganizationModal -> organization_listing_requests). */}
        <button
          type="button"
          onClick={() => setListOrgOpen(true)}
          className="inline-flex items-center gap-1.5 text-sm font-semibold hover:underline"
          style={{
            color: isDark ? 'var(--civic-gold)' : 'var(--civic-navy)',
            fontFamily: isAhp ? sans : undefined,
            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          }}
        >
          <Mail className="w-3.5 h-3.5" />
          List your organization on {brand.name} — it's free
        </button>
      </div>

      <ListOrganizationModal
        isOpen={listOrgOpen}
        onClose={() => setListOrgOpen(false)}
        isDark={isDark}
        scenarioName={scenarioName}
      />
    </section>
  );
}
